import { Injectable, computed, signal, inject } from '@angular/core';
import { ConfigService } from './config.service';
import { InitParamsService } from './init-params.service';
import { LayoutService } from './layout.service';
import { StoreIdService } from './store-id.service';

export interface SocialLinks {
  iOSAppId?: string;
  androidAppId?: string;
}

export enum OperatingSystem {
  android = 'android',
  iOS = 'iOS',
  iPad = 'iPad',
}

@Injectable({
  providedIn: 'root',
})
export class SocialLinksService {
  private readonly GOOGLE_PLAY_LINK = 'https://play.google.com/store/apps/details?id=';
  private readonly APPS_APPLE_LINK = 'https://apps.apple.com/app/id';
  private configService = inject(ConfigService);
  private initParamsService = inject(InitParamsService);
  private layoutService = inject(LayoutService);
  private storeId = inject(StoreIdService).storeId;  

  showBanner = signal<boolean>(false);
  bannerUrl = signal<string>('');
  isAppInstalled = signal<boolean>(false);//we need to first configure our app to implement this feature
  isAppleStore = signal<boolean>(false);

  socialLinks = computed(() => this.configService.configState().socialLinks);

  iOSAppUrl = computed(() => {
    return this.APPS_APPLE_LINK + this.socialLinks()?.iOSAppId;
  });

  androidAppUrl = computed(() => {
    return this.GOOGLE_PLAY_LINK + this.socialLinks()?.androidAppId;
  });

  constructor() {
    this.shouldOpenAppleStore();
    this.calcBanner();
    this.detectAppInstallation();
  }

  private detectAppInstallation(): void {
    //we need to first configure our app to implement this feature
    const isInstalled = false;
    this.isAppInstalled.set(isInstalled);
  }

  private shouldOpenAppleStore() {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any)['opera'];

    const isAppleDevice = (
        /iPad|iPhone|iPod/.test(userAgent) && 
        !(window as any)?.MSStream
    ) || (
        navigator.userAgent.includes('Mac') && 
        'ontouchend' in document
    );

    this.isAppleStore.set(isAppleDevice)
  }

  private calcBanner() {
    const wasBannerDismissed = localStorage.getItem(`MOBILE_BANNER_WAS_SEEN_${this.storeId}`) === 'true' ? true : false;

    if (wasBannerDismissed || this.socialLinks === null || this.initParamsService.isEmbedded === true || this.layoutService.mobileView() === false) {
      this.showBanner.set(false);
      return;
    }

    if (!this.socialLinks()?.androidAppId && !this.socialLinks()?.iOSAppId) return;

    //2 cases the app has either ios or android and we want to display only the existing
    if(!this.socialLinks()?.androidAppId && !this.isAppleStore()) return;
    if(!this.socialLinks()?.iOSAppId && this.isAppleStore()) return;
    
    this.showBanner.set(true);
    this.bannerUrl.set(this.isAppleStore() ? this.iOSAppUrl() : this.androidAppUrl())
  }

  dismissBanner() {
    this.showBanner.set(false);
    localStorage.setItem(`MOBILE_BANNER_WAS_SEEN_${this.storeId}`, 'true');
  }
}
